import { lightDark, SeasonalTokenOverrides, SeasonConfig } from '../types';
import { DynamicBackground } from './dynamic-background';
import { FallingParticles } from './falling-particles';
import leiSvg from './images/lei.svg';
import SummerLogoDark from './images/logo-dark.png';
import SummerLogoLight from './images/logo-light.png';
import { summerThemeCss } from './summer-theme-css';

const summerTokenOverrides: SeasonalTokenOverrides = {
	colors: {
		primary: {
			fg: lightDark('#279F95', '#5DDED4'),
			fgStrong: lightDark('#1E8A82', '#4DCBC0'),
			fgStronger: lightDark('#16756E', '#3DB8AE'),
			fgStrongest: lightDark('#0B5F59', '#2DA59B'),
			bg: lightDark('#279F95', '#279F95'),
			bgHover: lightDark('#1E8A82', '#1E8A82'),
			bgActive: lightDark('#0B5F59', '#0B5F59'),
			bgAlpha: lightDark('rgba(39, 159, 149, 0.12)', 'rgba(93, 222, 212, 0.15)'),
			bgAlphaHover: lightDark('rgba(39, 159, 149, 0.20)', 'rgba(93, 222, 212, 0.25)'),
			bgAlphaActive: lightDark('rgba(39, 159, 149, 0.28)', 'rgba(93, 222, 212, 0.35)'),
		},
		bg: {
			canvas: lightDark('#E7F1EA', 'transparent'),
			surface: lightDark('#FFFFFF', '#1D1F25'),
			soft: lightDark('#F0F7F3', '#252830'),
			subtle: lightDark('#EFF5F1', '#202329'),
			topNav: lightDark('#FFFFFF', '#161819'),
			inset: lightDark('#E7F1EA', '#131518'),
		},
		text: {
			interactive: lightDark('#279F95', '#5DDED4'),
		},
		border: {
			default: lightDark('#D1D5DB', '#3A3D45'),
			soft: lightDark('#E5E7EB', '#2A2D33'),
		},
	},
};

export const summerConfig: SeasonConfig = {
	themeClassName: 'summer-theme',
	toggleEmoji: '🌊',
	hideEmojiOnButton: true,

	colorModeStrategy: 'time-based',
	semanticTokenOverrides: summerTokenOverrides,

	modalVariant: 'scenic',

	logoLight: SummerLogoLight,
	logoDark: SummerLogoDark,

	modalSound: '',
	modalSoundVolume: 0,

	buttonTexts: [
		'☀️ Send some sunshine',
		'🌊 Make waves with praise',
		'🏖️ Shore up some kudos',
		'🍦 Sweeten their day',
		'🦩 Float some appreciation',
		'🐚 Shell out some thanks',
		'🌴 Paradise-grade praise',
		'🍉 Refresh a teammate',
	],

	modalDecorations: [],

	modalColors: {
		logoOverride: 'light',
		logoMaxWidth: '200px',
		overlayBg: 'blackAlpha.600',
		contentBg: 'bg.surface',
		borderColor: 'rgba(39, 159, 149, 0.25)',
		boxShadow: '0 24px 60px -20px rgba(39, 159, 149, 0.35), 0 4px 12px rgba(0, 0, 0, 0.06)',
		logoDropShadowColor: 'transparent',
		subtitleColor: '#0B5F59',
		enableButtonBg: '#1D7F77',
		enableButtonHoverBg: '#1A726B',
		enableButtonActiveBg: '#166660',
		enableButtonBoxShadow: '0 6px 18px -4px rgba(29, 127, 119, 0.45)',
		enableButtonHoverBoxShadow: '0 12px 28px -6px rgba(29, 127, 119, 0.55)',
		enableButtonTextColor: 'white',
		cancelButtonColor: '#0B5F59',
		cancelButtonHoverColor: '#279F95',
		noticeBg: 'transparent',
		noticeBorderColor: 'transparent',
		noticeTextColor: '#0B5F59',
		featureCardBg: 'rgba(231, 241, 234, 0.6)',
		featureCardBorderColor: 'rgba(39, 159, 149, 0.18)',
		featureCardTextColor: 'gray.700',
		featureBubbleGradient: 'linear-gradient(135deg, #FCE7F1 0%, #E7F1EA 100%)',
		featureBubbleBorderColor: 'rgba(229, 84, 143, 0.18)',
		closeButtonHoverBg: 'blackAlpha.100',
	},

	modalText: {
		subtitle: 'Take a vacation without going anywhere with Bonusly!',
		features: [
			{
				emoji: '🌊',
				text: 'Relax by a tropical, white sand beach',
			},
			{
				emoji: '😎',
				text: 'Enjoy floats that appear as you recognize others',
			},
			{
				emoji: '🍉',
				text: 'Get hungry as summer treats fall from the sky',
				toggleKey: 'bonusly:seasonal-theme:summer:show-falling-emojis',
			},
		],
		endDateNotice: 'This theme is available for a limited time. Turn it on/off at any time.',
		enableButtonText: 'Turn on Summer theme',
		cancelButtonText: 'Maybe later',
	},

	EffectsComponent: () => (
		<>
			<DynamicBackground />
			<FallingParticles />
		</>
	),

	themeCss: summerThemeCss,

	avatarOverlay: {
		image: leiSvg,
		scale: 1.4,
		anchor: { x: 0.5, y: 0.4375 },
	},
};
